
import { supabase } from "@/integrations/supabase/client";

export type EventType = "page_view" | "button_click";

interface TrackingOptions {
  extraData?: Record<string, any>;
  throttleMs?: number; // Throttle in milliseconds
  isSubscribed?: boolean; // Whether user is subscribed
  isLoggedIn?: boolean; // Whether user is logged in
  userEmail?: string | null; // User's email address
  location?: string | null; // Location string if available
}

// Store last tracked events to prevent duplicates within threshold
const recentEvents: Record<string, number> = {};

// Location tracking disabled to avoid requesting geolocation permissions
const fetchLocation = async (): Promise<string | null> => {
  return null;
};

/**
 * Track a user event (page view or button click)
 */
export const trackEvent = async (
  pagePath: string,
  eventType: EventType,
  options: TrackingOptions = {}
): Promise<boolean> => {
  const {
    extraData = {},
    throttleMs = 5000,
    userEmail = null,
    location = null,
  } = options;
  
  // Create a unique key for this event to prevent duplicates
  const eventKey = `${eventType}:${pagePath}:${JSON.stringify(extraData)}`;
  const now = Date.now();
  
  // Throttle events - don't track the same event within throttle window
  if (recentEvents[eventKey] && (now - recentEvents[eventKey] < throttleMs)) {
    console.log(`Tracking throttled for ${eventKey}`);
    return false;
  }
  
  // Update the last tracked time
  recentEvents[eventKey] = now;
  
  try {
    // Get current authenticated user (if any)
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    const email = userEmail || session?.user?.email || null;
    let loc = location;
    if (!loc) {
      loc = await fetchLocation();
    }
    
    const { error } = await supabase.from("page_events").insert([
      {
        page_path: pagePath,
        event_type: eventType,
        user_id: userId || null,
        user_email: email,
        extra_data: extraData,
        host_domain: window.location.hostname,
        location: loc
      }
    ]);
    
    if (error) {
      console.error("Error tracking event:", error);
      return false;
    }
    
    console.log(`Tracked ${eventType} for ${pagePath}`);
    return true;
  } catch (error) {
    console.error("Failed to track event:", error);
    return false;
  }
};

/**
 * Track a button click event
 */
export const trackButtonClick = (
  buttonId: string, 
  options: TrackingOptions = {}
) => {
  // Get current page path
  const pagePath = window.location.pathname;
  
  // Add button identifier to extra data
  const extraData = {
    buttonId,
    ...(options.isSubscribed !== undefined ? { isSubscribed: options.isSubscribed } : {}),
    ...(options.isLoggedIn !== undefined ? { isLoggedIn: options.isLoggedIn } : {}),
    ...options.extraData
  };
  
  return trackEvent(pagePath, "button_click", {
    ...options,
    extraData
  });
};
